import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { useRace } from '@/hooks/useRaces';
import { useRaceTypes } from '@/hooks/useRaceTypes';
import { useCreateRegistration } from '@/hooks/useRegistrations';
import { useTshirtSizes } from '@/hooks/useTshirtSizes';
import { RegistrationFormData } from './eventregistrationpage.props';
import EventRegistrationView from './eventregistrationpage.view';

const EventRegistrationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: raceData, isLoading: raceLoading } = useRace(id);
  const { data: raceTypesData = [], isLoading: typesLoading } = useRaceTypes(id);
  const createRegistration = useCreateRegistration();

  const [formData, setFormData] = useState<RegistrationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    country: 'Македонија',
    gender: 'Male',
    dateOfBirth: '',
    participantType: 'Individual',
    teamName: '',
    raceTypeId: '',
    tshirtSize: '',
    emergencyContactPhone: '',
    cookiesConsent: false,
    privacyPolicy: false
  });

  const { data: tshirtSizesData = [] } = useTshirtSizes(id, formData.raceTypeId || undefined);

  const prevRaceTypeIdRef = useRef<string>('');

  const loading = raceLoading || typesLoading;
  const submitting = createRegistration.isPending;

  const event = useMemo(() => {
    if (!raceData) return null;
    return {
      id: raceData.id,
      title: raceData.name,
      status: raceData.status?.toLowerCase() || 'upcoming'
    };
  }, [raceData]);

  const eventRaceTypes = useMemo(() => raceTypesData.map(rt => ({
    id: rt.id,
    name: rt.name,
    distance: rt.distance,
    price: rt.price,
    maxParticipants: rt.maxParticipants
  })), [raceTypesData]);

  useEffect(() => {
    if (eventRaceTypes.length > 0 && !formData.raceTypeId) {
      setFormData(prev => ({ ...prev, raceTypeId: eventRaceTypes[0].id }));
    }
  }, [eventRaceTypes, formData.raceTypeId]);

  useEffect(() => {
    const prev = prevRaceTypeIdRef.current;
    prevRaceTypeIdRef.current = formData.raceTypeId;
    if (prev && prev !== formData.raceTypeId) {
      setFormData(prev => ({ ...prev, tshirtSize: '' }));
    }
  }, [formData.raceTypeId]);

  useEffect(() => {
    if (tshirtSizesData.length > 0 && !formData.tshirtSize) {
      setFormData(prev => ({ ...prev, tshirtSize: tshirtSizesData[0].size }));
    }
  }, [tshirtSizesData, formData.tshirtSize]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    if (name === 'participantType' && value !== 'Team') {
      setFormData(prev => ({ ...prev, teamName: '' }));
    }
  };

  const calculatePrice = () => {
    const selectedRaceType = eventRaceTypes.find((rt) => rt.id === formData.raceTypeId);
    if (!selectedRaceType) return 0;
    return selectedRaceType.price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cookiesConsent || !formData.privacyPolicy) {
      toast({
        title: 'Грешка',
        description: 'Мора да ги прифатите условите за да продолжите.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const registrationData = {
        raceId: parseInt(id, 10),
        raceTypeId: parseInt(String(formData.raceTypeId), 10),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        country: formData.country,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        tshirtSize: formData.tshirtSize,
        participantType: formData.participantType,
        teamName: formData.participantType === 'Team' ? formData.teamName : null,
        discountCode: null,
        emergencyContactPhone: formData.emergencyContactPhone || null,
        cookiesConsent: formData.cookiesConsent,
        privacyPolicy: formData.privacyPolicy
      };

      await createRegistration.mutateAsync(registrationData);

      toast({
        title: 'Успешна регистрација!',
        description: `Се пријавивте за ${event?.title}. Вашата цена е ${calculatePrice()} МКД.`
      });

      setTimeout(() => {
        navigate(`/events/${id}`);
      }, 1500);
    } catch (err) {
      console.error('Error creating registration:', err);
      toast({
        title: 'Грешка',
        description: err.message || 'Неуспешна регистрација. Ве молиме обидете се повторно.',
        variant: 'destructive'
      });
    }
  };

  return (
    <EventRegistrationView
      event={event}
      eventRaceTypes={eventRaceTypes}
      tshirtSizes={tshirtSizesData}
      loading={loading}
      submitting={submitting}
      formData={formData}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      calculatePrice={calculatePrice}
      setFormData={setFormData}
      navigate={navigate}
    />
  );
};

export default EventRegistrationPage;
